import { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { ChevronLeft, Loader2, ArrowRightLeft, Calculator } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { toast } from 'sonner';
import { ItemForm } from '../components/ItemForm';
import { ItemPriceManagerTab } from '../components/ItemPriceManagerTab';
import { DocumentAttachments } from '@/app/components/DocumentAttachments';
import { StockSummaryCard } from './components/StockSummaryCard';
import { StockTransferDialog } from './components/StockTransferDialog';
import { StockReconciliationDialog } from './components/StockReconciliationDialog';
import { MobileTab } from './components/MobileTab';
import { getItem, createItem, updateItem } from '@/app/services/itemService';
import { getItemStock, listGodowns, getItemStockSummary } from '@/app/services/inventoryService';
import { listCategories } from '@/app/services/categoryService';
import { listUnits } from '@/app/services/unitService';
import { listTaxRates } from '@/app/services/taxRateService';
import { getOrganizationSettings } from '@/app/services/organizationSettingsService';
import { Item, ItemCreate, ItemUpdate } from '@/app/types/item';
import { CurrentStock } from '@/app/types/inventory';
import { useOrganization } from '@/app/hooks/useOrganization';
import { triggerItemSync } from '@/lib/indexeddb/syncUtils';

export default function ItemDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { currentOrganizationId } = useOrganization();
  const [activeTab, setActiveTab] = useState('info');

  const isEditMode = Boolean(id);
  const itemId = id ? parseInt(id, 10) : null;

  // Fetch item data
  const { data: item, isLoading: isLoadingItem, error: itemError } = useQuery({
    queryKey: ['item', itemId],
    queryFn: () => getItem(itemId!),
    enabled: Boolean(itemId),
  });

  // Fetch categories
  const { data: categoriesData, isLoading: isLoadingCategories } = useQuery({
    queryKey: ['categories', currentOrganizationId],
    queryFn: () => listCategories({ limit: 100 }, currentOrganizationId!),
    enabled: !!currentOrganizationId,
  });

  // Fetch units
  const { data: unitsData, isLoading: isLoadingUnits } = useQuery({
    queryKey: ['units', currentOrganizationId],
    queryFn: () => listUnits({ limit: 100 }, currentOrganizationId!),
    enabled: !!currentOrganizationId,
  });

  // Fetch godowns
  const { data: godownsData, isLoading: isLoadingGodowns } = useQuery({
    queryKey: ['godowns', currentOrganizationId],
    queryFn: () => listGodowns({ limit: 100 }),
    enabled: !!currentOrganizationId,
  });

  // Fetch tax rates
  const { data: taxRatesData, isLoading: isLoadingTaxRates } = useQuery({
    queryKey: ['tax-rates', currentOrganizationId],
    queryFn: () => listTaxRates({ limit: 100 }, currentOrganizationId!),
    enabled: !!currentOrganizationId,
  });

  // Fetch organization settings
  const { data: organizationSettings, isLoading: isLoadingOrgSettings } = useQuery({
    queryKey: ['organization-settings', currentOrganizationId],
    queryFn: () => getOrganizationSettings(),
    enabled: !!currentOrganizationId,
  });

  // Removed accounts fetching - SimpleAccountSelector handles this internally

  // Create mutation
  const createMutation = useMutation({
    mutationFn: (data: ItemCreate) => createItem(data),
    onSuccess: (newItem) => {
      toast.success('Item created successfully');
      queryClient.invalidateQueries({ queryKey: ['items'] });
      // Trigger sync after item creation
      // triggerItemSync(); // TEMPORARILY DISABLED for bulk data loading
      navigate(`/items/detail/${newItem.id}`);
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.detail || 'Failed to create item');
    },
  });

  // Update mutation
  const updateMutation = useMutation({
    mutationFn: (data: ItemUpdate) => updateItem(itemId!, data),
    onSuccess: () => {
      toast.success('Item updated successfully');
      queryClient.invalidateQueries({ queryKey: ['items'] });
      queryClient.invalidateQueries({ queryKey: ['item', itemId] });
      // Trigger sync after item update
      // triggerItemSync(); // TEMPORARILY DISABLED for bulk data loading
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.detail || 'Failed to update item');
    },
  });

  const handleSubmit = (data: ItemCreate | ItemUpdate) => {
    if (isEditMode) {
      updateMutation.mutate(data as ItemUpdate);
    } else {
      createMutation.mutate(data as ItemCreate);
    }
  };

  const handleBack = () => {
    navigate(-1); // Use browser back to preserve URL state
  };

  const isLoading = isLoadingItem || isLoadingCategories || isLoadingUnits || 
                    isLoadingGodowns || isLoadingTaxRates || isLoadingOrgSettings;

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin" />
      </div>
    );
  }

  if (itemError) {
    return (
      <Alert variant="destructive">
        <AlertDescription>
          Failed to load item: {(itemError as any).message}
        </AlertDescription>
      </Alert>
    );
  }

  // Extract data from API responses
  const categories = categoriesData?.items || [];
  const units = unitsData?.items || [];
  const godowns = godownsData?.items || [];
  const taxRates = taxRatesData?.items || [];

  return (
    <div className="container mx-auto py-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button
            variant="ghost"
            size="sm"
            onClick={handleBack}
            className="gap-2"
          >
            <ChevronLeft className="h-4 w-4" />
            Back to Items
          </Button>
          <h1 className="text-2xl font-bold">
            {isEditMode ? `Edit Item: ${item?.name}` : 'Create New Item'}
          </h1>
        </div>
      </div>

      {/* Main Content with Tabs */}
      <Card>
        <CardContent className="p-6">
          {isEditMode ? (
            <Tabs value={activeTab} onValueChange={setActiveTab}>
              <TabsList className="grid w-full grid-cols-5">
                <TabsTrigger value="info">Item Information</TabsTrigger>
                <TabsTrigger value="pricing">Pricing & Rules</TabsTrigger>
                <TabsTrigger value="mobile">Mobile</TabsTrigger>
                <TabsTrigger value="inventory">Inventory</TabsTrigger>
                <TabsTrigger value="documents">Documents</TabsTrigger>
              </TabsList>

              <TabsContent value="info" className="space-y-4 mt-6">
                <ItemForm
                  item={item}
                  onSubmit={handleSubmit}
                  isSubmitting={updateMutation.isPending}
                  categories={categories}
                  units={units}
                  godowns={godowns}
                  taxRates={taxRates}
                  organizationSettings={organizationSettings}
                />
              </TabsContent>

              <TabsContent value="pricing" className="space-y-4 mt-6">
                {item && <ItemPriceManagerTab itemId={item.id} itemName={item.name} />}
              </TabsContent>

              <TabsContent value="mobile" className="space-y-4 mt-6">
                <MobileTab item={item} />
              </TabsContent>

              <TabsContent value="inventory" className="space-y-4 mt-6">
                <InventoryTab itemId={itemId!} item={item} />
              </TabsContent>

              <TabsContent value="documents" className="space-y-4 mt-6">
                <DocumentAttachments
                  entityType="item"
                  entityId={itemId!}
                  title="Item Documents"
                  maxFileSize={10}
                  maxFiles={20}
                  enableMetadata={true}
                />
              </TabsContent>
            </Tabs>
          ) : (
            <ItemForm
              onSubmit={handleSubmit}
              isSubmitting={createMutation.isPending}
              categories={categories}
              units={units}
              godowns={godowns}
              taxRates={taxRates}
              organizationSettings={organizationSettings}
            />
          )}
        </CardContent>
      </Card>
    </div>
  );
}

// Inventory Tab Component
function InventoryTab({ itemId, item }: { itemId: number; item?: any }) {
  const [transferDialogOpen, setTransferDialogOpen] = useState(false);
  const [reconcileDialogOpen, setReconcileDialogOpen] = useState(false);

  // Fetch godowns
  const { data: godownsData } = useQuery({
    queryKey: ['godowns'],
    queryFn: () => listGodowns({ limit: 100 }),
  });

  // Fetch stock summary
  const { data: stockSummary, isLoading, error } = useQuery({
    queryKey: ['item-stock-summary', itemId],
    queryFn: () => getItemStockSummary(itemId),
    enabled: Boolean(itemId),
  });

  // Get user roles from auth context or store
  const userRoles = ['org_admin', 'data_entry']; // TODO: Get from actual auth context
  const canReconcile = userRoles.includes('org_admin');

  const godowns = godownsData?.items || [];

  if (item?.item_type !== 'product') {
    return (
      <Alert>
        <AlertDescription>
          Inventory management is only available for product items.
        </AlertDescription>
      </Alert>
    );
  }

  return (
    <div className="space-y-6">
      {/* Action Buttons */}
      <div className="flex justify-between items-center">
        <h3 className="text-lg font-medium">Inventory Management</h3>
        <div className="flex gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => setTransferDialogOpen(true)}
            disabled={!stockSummary || stockSummary.total_stock === '0'}
          >
            <ArrowRightLeft className="h-4 w-4 mr-2" />
            Transfer Stock
          </Button>
          {canReconcile && (
            <Button
              variant="outline"
              size="sm"
              onClick={() => setReconcileDialogOpen(true)}
            >
              <Calculator className="h-4 w-4 mr-2" />
              Reconcile Stock
            </Button>
          )}
        </div>
      </div>

      {/* Stock Summary Card */}
      <StockSummaryCard
        stockSummary={stockSummary}
        item={item}
        isLoading={isLoading}
        error={error}
      />

      {/* Stock Transfer Dialog */}
      {transferDialogOpen && (
        <StockTransferDialog
          open={transferDialogOpen}
          onOpenChange={setTransferDialogOpen}
          item={item}
          godowns={godowns}
          stockSummary={stockSummary}
        />
      )}

      {/* Stock Reconciliation Dialog */}
      {reconcileDialogOpen && (
        <StockReconciliationDialog
          open={reconcileDialogOpen}
          onOpenChange={setReconcileDialogOpen}
          item={item}
          godowns={godowns}
          stockSummary={stockSummary}
        />
      )}
    </div>
  );
}

